import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [listings, setListings] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_id: "", property_title: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/listings`).then(res => setListings(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", property_id: "", property_title: "", message: "" });
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePropertyChange = (e) => {
    const val = e.target.value;
    if (val) {
      const listing = listings.find(l => l.id === val);
      setForm(f => ({ ...f, property_id: val, property_title: listing?.title || "" }));
    } else {
      setForm(f => ({ ...f, property_id: "", property_title: "" }));
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white pt-20 min-h-screen">
      <section className="px-6 md:px-12 lg:px-24 py-16 border-b border-[#E2E8F0]">
        <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">Get In Touch</p>
        <h1 className="font-['DM_Serif_Display'] text-5xl sm:text-6xl tracking-tight text-[#1A1A2E] mb-4">Contact Us</h1>
        <p className="text-[#64748B] text-base max-w-xl">
          Have a question about our properties or interested in booking? We'd love to hear from you.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="flex flex-col gap-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input
                  type="text"
                  placeholder="Name *"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="contact-name"
                  className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  data-testid="contact-email"
                  className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input
                  type="tel"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  data-testid="contact-phone"
                  className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm rounded"
                />
                <select
                  value={form.property_id}
                  onChange={handlePropertyChange}
                  data-testid="contact-property"
                  className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full text-sm cursor-pointer rounded"
                >
                  <option value="">Select Property (Optional)</option>
                  {listings.map(l => (
                    <option key={l.id} value={l.id}>{l.title}</option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder="Message *"
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                rows={5}
                data-testid="contact-message"
                className="bg-white border border-[#E2E8F0] focus:border-[#32A334] text-[#1A1A2E] py-3 px-4 outline-none transition-colors w-full placeholder:text-[#94A3B8] text-sm resize-none rounded"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm font-semibold self-start disabled:opacity-50 rounded"
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-5">
            <div className="p-8 border border-[#E2E8F0] bg-[#F4F6FA] rounded-lg">
              <h3 className="font-['DM_Serif_Display'] text-2xl text-[#1A1A2E] mb-8">Contact Information</h3>
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#32A334] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1">Managed by</p>
                    <p className="text-[#1A1A2E] text-sm font-medium mb-3">Bergrand Realty</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1">Email</p>
                    <p className="text-[#1A1A2E] text-sm">rick@bergrand.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#32A334] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] mb-1">Phone</p>
                    <p className="text-[#1A1A2E] text-sm">(231) 221-2323</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
